// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Button, { ButtonPresentationProps } from "@sc/MobiusOverrides/Button/Button";
import { XNGAGE_CUSTOM_WIDGET } from "@sc/XDL/constants";
import React from "react";

const enum fields {
    text = "text",
    firstButtonLabel = "firstButtonLabel",
    firstButtonLink = "firstButtonLink",
    secondButtonLabel = "secondButtonLabel",
    secondButtonLink = "secondButtonLink",
}

interface Props extends WidgetProps {
    fields: {
        [fields.text]: string;
        [fields.firstButtonLabel]: string;
        [fields.firstButtonLink]: LinkFieldValue;
        [fields.secondButtonLabel]: string;
        [fields.secondButtonLink]: LinkFieldValue;
    };
}

export interface FooterApplicationStyles {
    container?: InjectableCss;
    text?: TypographyPresentationProps;
    firstButton?: ButtonPresentationProps;
    secondButton?: ButtonPresentationProps;
    buttonsContainer?: InjectableCss;
}

export const footerApplicationStyles: FooterApplicationStyles = {};

const FooterApplication: React.FC<Props> = ({ id, fields }) => {
    const styles = footerApplicationStyles;
    const firstButtonLink = useGetLink(fields.firstButtonLink);
    const secondButtonLink = useGetLink(fields.secondButtonLink);

    const handleOnClick = (link: Readonly<PageLinkModel> | { url: string | undefined; title: string | undefined }) => {
        if (!link.url) {
            return;
        }

        window.open(link.url);
    };

    return (
        <StyledWrapper {...styles.container}>
            <Typography {...styles.text}>{fields.text}</Typography>
            <StyledWrapper {...styles.buttonsContainer}>
                {fields.firstButtonLabel && firstButtonLink && (
                    <Button
                        aria-label={fields.firstButtonLabel}
                        onClick={() => {
                            handleOnClick(firstButtonLink);
                        }}
                        {...styles.firstButton}
                    >
                        {fields.firstButtonLabel}
                    </Button>
                )}
                {fields.secondButtonLabel && secondButtonLink && (
                    <Button
                        aria-label={fields.secondButtonLabel}
                        onClick={() => {
                            handleOnClick(secondButtonLink);
                        }}
                        {...styles.secondButton}
                    >
                        {fields.secondButtonLabel}
                    </Button>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const footerApplication: WidgetModule = {
    component: FooterApplication,
    definition: {
        group: XNGAGE_CUSTOM_WIDGET,
        allowedContexts: ["Footer"],
        fieldDefinitions: [
            {
                name: fields.text,
                displayName: "Text",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.firstButtonLabel,
                displayName: "First Button Label",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.firstButtonLink,
                displayName: "First Button Link",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                fieldType: "General",
            },
            {
                name: fields.secondButtonLabel,
                displayName: "Second Button Label",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.secondButtonLink,
                displayName: "Second Button Link",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                fieldType: "General",
            },
        ],
    },
};

export default footerApplication;
